import React, {
	useState,
	useMemo,
	useEffect,
	useContext,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { IconCopySolid, IconInfoCircle, IconUpload2Solid } from '@brandlive/pixel';
import { closeMetaMenu } from '../../stores/actions/tabs';
import ButtonClose from '../general-components/button-close';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { setModalState } from '../../stores/actions/modal';
import { MODAL_AVATAR_ACTIONS, MODAL_SAFETY_PHOTO_ACTIONS, MODAL_TEMPLATES } from '../../stores/reducers/modal';
import TextInput from '../general-components/text-input';
import { showAlert } from '../general-components/alert-service';
import { GreenroomState as GS } from '../../stores/types/types';
import LoadingSpinner, { LoadingSpinnerType } from '../general-components/loading-spinner';
import { CreateSrtStream, GetPreviousLocationsForMeeting, UpdateOnsiteLocation } from '../../connection/video';
import { setSelectedOnsiteLocationId } from '../../stores/actions/video';
import type { OnsiteLocation, PreviousLocation } from '../../stores/types/video';
import Switch from '../general-components/switch';
import WaitingIndicator from '../general-components/waiting-indicator';
import {
	IMG_TYPES,
	MEDIA_UPLOAD_ERROR_TYPES,
	UPLOAD_STATUS,
	UPLOAD_TYPES,
	VID_TYPES,
} from '../../types/constants';
import {
	buildMediaUrlPrefix,
	checkFileName,
	createMediaFile,
	getTags,
	getFileExtensionGivenFile,
	getVideoStill,
	mediaUploadError,
	resizeImage,
	uploadImageVideoToS3,
} from '../../utils/media';
import { S3_SUB_DIRECTORY, UploadCloudConvert } from '../../connection/media';
import { UploadContext } from '../../contexts/upload-context';
import { isVideo } from '../../utils/utils';
import { setSolutionCenterContentState } from '../../stores/actions/user-interface';
import { SolutionCenterArticles, SolutionCenterContentTypes } from '../../types/solution-center';
import TooltipWrapper, { TTPosition } from '../general-components/tooltips/tooltip-wrapper';
import { EntityType } from '../../types/enums';
import { STATIC_ASSET_URL } from '../../utils/content-templates';
import useClickAwayListener from '../../hooks/use-click-away-listener';
import {
	isLivekitIngressInputType,
	LIVEKIT_INGRESS_INPUT_LABELS,
	type LivekitIngressInputType,
} from '../../stores/types/livekit';

export const SRT_STREAM_URL = process.env.REACT_APP_SRT_STREAM_URL || 'srt://srt.brandlive.com';

export default function MetaOnsiteLocation() {
	const dispatch = useDispatch();

	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const selectedOnsiteLocationId = useSelector((s: GS) => s.VideoReducer.selectedOnsiteLocationId);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const livekitIngresses = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const {
		use_whip_streams: useWhipStreams,
		use_pixel: usePixel,
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	// dropdown arrow/caret render smaller without the pixel (v3) design
	const dropdownIconSize = usePixel ? 16 : 12;

	const [locationName, setLocationName] = useState<string>('');
	const [previousDropdownOpen, setPreviousDropdownOpen] = useState<boolean>(false);
	const [previousLocations, setPreviousLocations] = useState<PreviousLocation[]>([]);
	const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
	const [showSrtCredentials, setShowSrtCredentials] = useState<boolean>(false);
	const [showIngressCredentials, setShowIngressCredentials] = useState<boolean>(false);
	const [creatingSrtStream, setCreatingSrtStream] = useState<boolean>(false);
	const [inputTypeSelectorOpen, setInputTypeSelectorOpen] = useState<boolean>(false);

	const holdSlideInput = useRef<HTMLInputElement>(null);
	const previousLocationsRef = useRef<HTMLDivElement>(null);

	const { addUpload, holdSlideUploads } = useContext(UploadContext);

	useClickAwayListener({
		ref: previousLocationsRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setPreviousDropdownOpen(false),
		open: previousDropdownOpen,
	});

	const selectedLocation = useMemo(
		() => onsiteLocations.find((location) => location.id === selectedOnsiteLocationId),
		[onsiteLocations, selectedOnsiteLocationId],
	);

	const selectedSrtStream = useMemo(
		() => (selectedLocation?.srtId
			? srtStreams.find((stream) => stream.id === selectedLocation.srtId)
			: null),
		[srtStreams, selectedLocation?.srtId],
	);

	const effectiveInputType = useMemo(() => {
		if (!selectedLocation) return 'cam_mic' as const;
		if (useWhipStreams && selectedLocation.inputType) return selectedLocation.inputType;
		return selectedLocation.useSrt ? 'srt' : 'cam_mic';
	}, [useWhipStreams, selectedLocation?.inputType, selectedLocation?.useSrt]);

	const ingressInputType = useMemo(
		() => (isLivekitIngressInputType(effectiveInputType) ? effectiveInputType : null),
		[effectiveInputType],
	);

	const selectedIngress = useMemo(() => {
		if (!selectedLocation?.inputId || !ingressInputType) return null;
		return livekitIngresses.find((i) => i.id === selectedLocation.inputId) ?? null;
	}, [livekitIngresses, selectedLocation?.inputId, ingressInputType]);

	const handleCloseInputTypeSelector = () => {
		setInputTypeSelectorOpen(false);
		window.removeEventListener('click', handleCloseInputTypeSelector);
	};

	const handleOpenInputTypeSelector = () => {
		if (!inputTypeSelectorOpen) {
			setInputTypeSelectorOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleCloseInputTypeSelector);
			}, 100);
		}
	};

	const handleSetInputType = async (newType: 'cam_mic' | 'srt' | LivekitIngressInputType) => {
		if (!selectedLocation || !token || !meeting) return;
		if (newType === effectiveInputType) return;

		if (newType === 'srt') {
			if (!selectedLocation.srtId && !creatingSrtStream) {
				setCreatingSrtStream(true);
				const entity = await CreateSrtStream(
					token,
					meeting.meeting,
					'SRT Feed',
					`${STATIC_ASSET_URL}/default-images/srt-safety-image.png`,
				);
				if (!entity || entity.entityType !== EntityType.srtStream) {
					console.error('Failed to create SRT stream');
					setCreatingSrtStream(false);
					return;
				}
				const streamId = Number(entity.entityId);
				handleUpdateOnsiteLocation({ inputType: 'srt', inputId: String(streamId), srtId: streamId });
			} else {
				handleUpdateOnsiteLocation({ inputType: 'srt', inputId: String(selectedLocation.srtId) });
			}
		} else if (isLivekitIngressInputType(newType)) {
			// sync provisions (or reconnects) the LiveKit ingress for the location off inputType
			handleUpdateOnsiteLocation({ inputType: newType });
			setShowSrtCredentials(false);
			setShowIngressCredentials(false);
		} else {
			handleUpdateOnsiteLocation({ inputType: 'cam_mic', inputId: null });
			setShowSrtCredentials(false);
			setShowIngressCredentials(false);
		}
	};

	const holdSlideThumbnail = useMemo(() => {
		if (!selectedLocation?.holdSlide) return <label className="bl-font-l2 grayscale-400">Black only</label>;
		const url = isVideo(selectedLocation?.holdSlide ?? '')
			? getVideoStill(selectedLocation.holdSlide, S3_SUB_DIRECTORY.THUMBS)
			: selectedLocation.holdSlide;
		return <img src={url} alt="Hold Slide" />;
	}, [selectedLocation?.holdSlide]);

	const holdSlideUploading = useMemo(
		() => holdSlideUploads.find((u) => u.uploadIdentifier === `hold-slide-${selectedOnsiteLocationId}`),
		[holdSlideUploads, selectedOnsiteLocationId],
	);

	const holdSlideUploadingOrConverting = useMemo(
		() => !!holdSlideUploading || !!selectedLocation?.cloudConvertId,
		[holdSlideUploading, selectedLocation?.cloudConvertId],
	);

	const openAvatarPhotoModal = () => {
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.SAFETY_PHOTO,
			modalData: {
				isOnboarding: false,
				modalAvatarAction: MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_ONSITE_LOCATION,
				onsiteLocationId: selectedOnsiteLocationId,
			},
		}));
	};

	const copyToClipboard = (value: string, type?: string) => {
		navigator.clipboard.writeText(value);
		showAlert({
			message: type ? 'Copied' : 'Link copied',
			description: type ? `${type} is attached to your clipboard` : 'Onsite URL is attached to your clipboard',
			type: 'general-notification',
			duration: 3000,
		});
	};

	const handleUpdateOnsiteLocation = (props: Partial<OnsiteLocation>) => {
		if (!token || !meeting) return;
		UpdateOnsiteLocation(
			token,
			meeting.meeting,
			selectedOnsiteLocationId,
			props,
		);
	};

	const handleUpdateLocationName = (newName: string) => {
		if (!selectedLocation || newName === selectedLocation.locationName) return;
		const selectedPreviousLocation = previousLocations.find((l) => l.locationName === newName);
		handleUpdateOnsiteLocation({
			locationName: newName,
			safetyImage: selectedPreviousLocation?.safetyImage || selectedLocation.safetyImage,
			holdSlide: selectedPreviousLocation?.holdSlide || selectedLocation.holdSlide,
		});
	};

	const handleLocationNameChange = (newValue: string) => {
		setLocationName(newValue);

		if (debounceTimeout) clearTimeout(debounceTimeout);

		const timeout = setTimeout(() => {
			handleUpdateLocationName(newValue);
		}, 2000);
		setDebounceTimeout(timeout);
	};

	const handleOpenOptions = async () => {
		if (!previousDropdownOpen && token && meeting) {
			const results = await GetPreviousLocationsForMeeting(token, meeting.meeting);
			setPreviousLocations(results);
			if (results.length === 0) return;
			setPreviousDropdownOpen(true);
		}
	};

	const handleClickUpload = () => {
		holdSlideInput.current?.click();
	};

	const uploadFile = async (
		file: File,
		extension: string,
	) => {
		if (!token || !meeting || !selectedLocation) return;

		const list = new DataTransfer();
		list.items.add(createMediaFile(file));
		const filesWithRandomKey: FileList = list.files;

		// check file name for unsupported characters
		if (!checkFileName(filesWithRandomKey[0].name)) return;

		const prefix = buildMediaUrlPrefix(meeting.channel, meeting.meeting_id);

		// upload image directly to S3
		if (IMG_TYPES.includes(extension)) {
			const fileToUpload = await resizeImage(filesWithRandomKey[0], extension, 1920, 1080);

			const imageUrl = await uploadImageVideoToS3(
				fileToUpload,
				prefix,
				meeting.channel,
				(request) => {
					addUpload(
						fileToUpload.name,
						UPLOAD_TYPES.HOLD_SLIDE,
						`hold-slide-${selectedOnsiteLocationId}`,
						request,
					);
				},
			);

			if (imageUrl) handleUpdateOnsiteLocation({ holdSlide: imageUrl });
		}

		// convert video using cloud convert
		if (VID_TYPES.includes(extension)) {
			const ext = getFileExtensionGivenFile(filesWithRandomKey[0]);
			const tags = getTags('t:hold_slide');
			const result = await UploadCloudConvert(
				token,
				meeting.channel,
				filesWithRandomKey,
				ext,
				prefix,
				tags,
				(request) => {
					addUpload(
						filesWithRandomKey[0].name,
						UPLOAD_TYPES.HOLD_SLIDE,
						`hold-slide-${selectedOnsiteLocationId}`,
						request,
					);
				},
			);

			if (result.status === UPLOAD_STATUS.LOAD && result.data) {
				handleUpdateOnsiteLocation({ cloudConvertId: result.data.id });
			} else if (result.status === UPLOAD_STATUS.ERROR) {
				mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
			}
		}
	};

	const cropHoldSlide = (file: File, extension: string) => {
		const objectUrl = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			const sixteenByNine = (
				((img.width / img.height) > 1.74)
				&& ((img.width / img.height) < 1.8)
			);
			if (sixteenByNine) {
				uploadFile(file, extension);
			} else {
				dispatch(setModalState({
					modalOpen: true,
					modalTemplate: MODAL_TEMPLATES.AVATAR_PHOTO,
					modalData: {
						isOnboarding: false,
						modalAvatarAction: MODAL_AVATAR_ACTIONS.HOLD_SLIDE,
						onsiteLocationId: selectedOnsiteLocationId,
						existingInfo: { url: objectUrl, fileType: file.type, name: file.name },
						uploadCallback: (croppedFile: File) => uploadFile(croppedFile, extension),
					},
				}));
			}
		};
		img.src = objectUrl;
	};

	const handleChangeFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || e.target.files.length === 0) return;

		const extension = getFileExtensionGivenFile(e.target.files[0]);

		if (IMG_TYPES.includes(extension)) {
			cropHoldSlide(e.target.files[0], extension);
		} else {
			uploadFile(e.target.files[0], extension);
		}

		e.target.value = '';
	};

	const handleOpenSolutionCenter = () => {
		dispatch(setSolutionCenterContentState({
			id: SolutionCenterArticles.FeatureOverviewLocations,
			type: SolutionCenterContentTypes.Article,
		}));
	};

	const handleToggleSrt = async () => {
		if (!selectedLocation || !token || !meeting || (!selectedLocation.srtId && creatingSrtStream)) return;

		// create a new srt stream if there isn't one associated with this location
		if (!selectedLocation.srtId) {
			setCreatingSrtStream(true);
			const entity = await CreateSrtStream(
				token,
				meeting.meeting,
				'SRT Feed',
				`${STATIC_ASSET_URL}/default-images/srt-safety-image.png`,
			);
			if (!entity || entity.entityType !== EntityType.srtStream) {
				console.error('Failed to create SRT stream');
				setCreatingSrtStream(false);
				return;
			}
			const streamId = Number(entity.entityId);
			handleUpdateOnsiteLocation({ srtId: streamId, useSrt: !selectedLocation.useSrt });
		} else {
			handleUpdateOnsiteLocation({ useSrt: !selectedLocation.useSrt });
		}

		if (!selectedLocation.useSrt) {
			showAlert({
				message: 'SRT Feed created',
				description: 'Your credentials are available within the edit panel',
				type: 'srt-notification',
				duration: 3000,
			});
		}

		setShowSrtCredentials(false);
	};

	useEffect(() => {
		if (selectedLocation && locationName !== selectedLocation.locationName) {
			setLocationName(selectedLocation.locationName);
		}
	}, [selectedLocation]);

	useEffect(() => {
		if (!selectedOnsiteLocationId) {
			dispatch(closeMetaMenu());
		}
	}, [selectedOnsiteLocationId]);

	useEffect(() => {
		if (creatingSrtStream && selectedLocation?.srtId) {
			setCreatingSrtStream(false);
		}
	}, [creatingSrtStream, selectedLocation]);

	return (
		!selectedLocation ? (
			<LoadingSpinner
				height={36}
				width={36}
				type={LoadingSpinnerType.Gradient}
				style={{ background: '#26242B' }}
			/>
		) : (
			<div className="meta-panel-container">
				<div className="meta-panel-header-container">
					<h5 className="bl-font-header">Location Info</h5 >
					<ButtonClose noPadding onClick={() => dispatch(setSelectedOnsiteLocationId(0))} />
				</div >
				<div className="meta-panel-body">
					<div onClick={() => { openAvatarPhotoModal(); }} className="bg-image-picker">
						<img src={selectedLocation.safetyImage} alt={'Safety Image'}/>
						<button className="upload-button-profile" onClick={() => { openAvatarPhotoModal(); }}>
							<IconUpload2Solid height="12" width="12" />
						</button>
					</div>
					<div className="meta-panel-input-container margin-top-medium margin-bottom-small">
						<div
							ref={previousLocationsRef}
							className="location-input-container position-relative"
							onFocus={handleOpenOptions}
						>
								<TextInput
									placeholder='Location Name'
									value={locationName}
									onChange={(e) => handleLocationNameChange(e.target.value)}
									onBlur={() => {
										if (debounceTimeout) clearTimeout(debounceTimeout);
										handleUpdateLocationName(locationName);
									}}
								/>
							<div
								className={classNames('previous-locations-popover', previousDropdownOpen && 'open')}
							>
								<div className="select-option header">
									<span className="bl-font-l3">PREVIOUS LOCATIONS</span>
								</div>
								{previousLocations.map((previous, index) => (
									<div
										key={index}
										className="select-option"
										onMouseUp={() => {
											handleUpdateLocationName(previous.locationName);
											setPreviousDropdownOpen(false);
										}}
									>
										<span className="bl-font-p2 truncate-overflow">{previous.locationName}</span>
									</div>
								))}
							</div>
						</div>
						<TextInput
							placeholder='Onsite Location URL'
							value={`${window.location.origin}/${selectedLocation.pathName}/${meeting!.meeting_id}`}
							disabled={true}
							icon={<IconCopySolid height="12" width="12" />}
							iconPosition='right'
							iconOnClick={(val: string) => copyToClipboard(val)}
							customClassNameProp='copy-input'
						/>
					</div>
					{useWhipStreams && ingressInputType && (
						<div className="meta-panel-srt-credentials-container">
							<div className="flex-align-center justify-content-space-between">
								<span className="flex-align-center bl-font-s1 gap-extra-small">
									<Icon name={ICONS.FILM_CAMERA} size={16} color={COLORS.WHITE} />
									{`${LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType]} Connection`}
									<div style={{ marginTop: '-6px' }}>
										<TooltipWrapper
											message={`Input these credentials into any ${LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType]} enabled stream encoder`}
											position={TTPosition.above4px}
											classNames="srt-connection"
										>
											<div className="info-i-tooltip">
												<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
											</div>
										</TooltipWrapper>
									</div>
								</span>
							</div>
							{selectedIngress && (
								<div className="meta-panel-srt-credentials-dropdown">
									<button
										className={classNames('meta-panel-srt-credentials-button', showIngressCredentials && 'open')}
										onClick={() => setShowIngressCredentials(!showIngressCredentials)}
									>
										<span className="bl-font-s1 grayscale-100">Credentials</span>
										<Icon
											name={showIngressCredentials ? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
											size={16}
											color={COLORS.TEXT_GRAY_200}
										/>
									</button>
									<div className={classNames('meta-panel-srt-credentials', showIngressCredentials && 'open')}>
										<div className="meta-panel-input-container">
											<TextInput
												placeholder='URL'
												value={selectedIngress.endpoint}
												disabled={true}
												icon={<IconCopySolid height="12" width="12" />}
												iconPosition='right'
												iconOnClick={() => copyToClipboard(selectedIngress.endpoint, 'Endpoint')}
												customClassNameProp='copy-input'
											/>
											{selectedIngress.streamKey && (
												<TextInput
													placeholder='Stream Key'
													value={selectedIngress.streamKey}
													disabled={true}
													icon={<IconCopySolid height="12" width="12" />}
													iconPosition='right'
													iconOnClick={() => copyToClipboard(selectedIngress.streamKey!, 'Stream Key')}
													customClassNameProp='copy-input'
												/>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
					{effectiveInputType === 'srt' && selectedSrtStream && (
						<div className="meta-panel-srt-credentials-container">
							<div className="flex-align-center justify-content-space-between">
								<span className="flex-align-center bl-font-s1 gap-extra-small">
									<Icon name={ICONS.FILM_CAMERA} size={16} color={COLORS.WHITE} />
									SRT Connection
									<div style={{ marginTop: '-6px' }}>
										<TooltipWrapper
											message="Input these credentials into any SRT enabled stream encoder"
											position={TTPosition.above4px}
											classNames="srt-connection"
										>
											<div className="info-i-tooltip">
												<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
											</div>
										</TooltipWrapper>
									</div>
								</span>
							</div>
							<div className="meta-panel-srt-credentials-dropdown">
								<button
									className={classNames('meta-panel-srt-credentials-button', showSrtCredentials && 'open')}
									onClick={() => setShowSrtCredentials(!showSrtCredentials)}
								>
									<span className="bl-font-s1 grayscale-100">Credentials</span>
									<Icon
										name={showSrtCredentials ? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
										size={16}
										color={COLORS.TEXT_GRAY_200}
									/>
								</button>
								<div className={classNames('meta-panel-srt-credentials', showSrtCredentials && 'open')}>
									<div className="meta-panel-input-container">
										<TextInput
											placeholder='URL'
											value={`${SRT_STREAM_URL}:6001`}
											disabled={true}
											icon={<IconCopySolid height="12" width="12" />}
											iconPosition='right'
											iconOnClick={() => copyToClipboard(`${SRT_STREAM_URL}:6001`, 'URL')}
											customClassNameProp='copy-input'
										/>
										<TextInput
											placeholder='Stream ID'
											value={selectedSrtStream.streamKey}
											disabled={true}
											icon={<IconCopySolid height="12" width="12" />}
											iconPosition='right'
											iconOnClick={() => copyToClipboard(selectedSrtStream.streamKey, 'Stream ID')}
											customClassNameProp='copy-input'
										/>
										<TextInput
											placeholder='Pass Phrase'
											value={selectedSrtStream.srtPassphrase}
											disabled={true}
											icon={<IconCopySolid height="12" width="12" />}
											iconPosition='right'
											iconOnClick={() => copyToClipboard(selectedSrtStream.srtPassphrase, 'Pass Phrase')}
											customClassNameProp='copy-input'
										/>
									</div>
									<div className="flex-justify-flex-end">
										<span
											className="bl-font-s1 text-green cursor-pointer"
											onClick={() => {
												const url = `${SRT_STREAM_URL}:6001`;
												const streamId = `streamid=${selectedSrtStream.streamKey}`;
												const passphrase = `passphrase=${selectedSrtStream.srtPassphrase}`;
												const srtString = `${url}?${streamId}&${passphrase}`;
												copyToClipboard(srtString, 'Full URL');
											}}
										>
											Copy all
										</span>
									</div>
								</div>
							</div>
						</div>
					)}
					<div>
						<div className="flex-align-center justify-content-space-between">
							<span className="flex-align-center bl-font-s1 gap-extra-small">
								<Icon name={ICONS.SPARKLE} size={16} color={COLORS.WHITE} />
								Smart Canvas
								<div style={{ marginTop: '-6px' }}>
									<TooltipWrapper
										message="Provides a unique canvas configuration for each location"
										position={TTPosition.above4px}
										classNames="smart-canvas"
									>
										<div className="info-i-tooltip">
											<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
										</div>
									</TooltipWrapper>
								</div>
							</span>
							<Switch
								on={selectedLocation.hideSelfView}
								onChange={() => handleUpdateOnsiteLocation(
									{ hideSelfView: !selectedLocation.hideSelfView },
								)}
								className='green'
							/>
						</div>
						<div className="hold-slide-container">
							<input
								type="file"
								ref={holdSlideInput}
								style={{ display: 'none' }}
								onChange={handleChangeFileInput}
								accept={[...VID_TYPES, ...IMG_TYPES].map((a) => `.${a}`).join(',')}
							/>
							<div className="hold-slide-data">
								<div className="slide-preview">{holdSlideThumbnail}</div>
								<div className="hold-slide-description">
									<span className="flex-align-center bl-font-s1 gap-extra-small">
										<Icon name={ICONS.PRIVATE} size={16} color={COLORS.WHITE} />
										Hold slide
									</span>
									<span className="bl-font-c2 hold-slide-subtext">
										Displayed onsite when there is nothing to display on canvas
									</span>
								</div>
							</div>
							<div className="align-items-center justify-content-space-between width-full">
								<div className="flex gap-extra-small">
									<span className="bl-font-p2 grayscale-200">Use most recent slide deck</span>
									<TooltipWrapper
										message="This will override your hold slide"
										classNames="recent-slide-deck"
										position={TTPosition.above4px}
									>
										<div className="info-i-tooltip">
											<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
										</div>
									</TooltipWrapper>
								</div>
								<Switch
									on={selectedLocation.useSlideDeck}
									onChange={() => handleUpdateOnsiteLocation(
										{ useSlideDeck: !selectedLocation.useSlideDeck },
									)}
									className='green'
								/>
							</div>
							<div className="hold-slide-actions">
								{selectedLocation.holdSlide ? (
									<>
										<button onClick={handleClickUpload} disabled={holdSlideUploadingOrConverting}>
											{holdSlideUploadingOrConverting ? (
												<WaitingIndicator />
											) : (
												<>
													<Icon name={ICONS.UPLOAD} size={12} color={COLORS.TEXT_GRAY_200} />
													<label className="bl-font-l1 cursor-pointer">Replace</label>
												</>
											)}
										</button>
										<button
											className="hold-slide-delete-button"
											onClick={() => handleUpdateOnsiteLocation({ holdSlide: '' })}
											disabled={holdSlideUploadingOrConverting}
										>
											<Icon name={ICONS.MEDIA_DELETE} size={12} color={COLORS.SEMANTIC_RED_400} />
											<label className="bl-font-l1 semantic-red-400 cursor-pointer">Delete</label>
										</button>
									</>
								) : (
										<button onClick={handleClickUpload} disabled={holdSlideUploadingOrConverting}>
										{holdSlideUploadingOrConverting ? (
											<WaitingIndicator />
										) : (
											<>
												<Icon name={ICONS.UPLOAD} size={12} color={COLORS.TEXT_GRAY_200} />
												<label className="bl-font-l1 cursor-pointer">Upload</label>
											</>
										)}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className='meta-panel-action-button-gradient'></div>
				<div className="meta-panel-action-button-wrapper">
					<div className="meta-panel-action-button-container">
						<div className="action-slot">
							<button className="meta-panel-action-button bl-font-l1" onClick={handleOpenSolutionCenter}>
								<IconInfoCircle height="16" width="16" />
								Learn More
							</button>
						</div>
						{useWhipStreams ? (
							effectiveInputType === 'cam_mic' ? (
								<div className="action-slot input-type-selector">
									<button
										className="meta-panel-action-button bl-font-l1"
										disabled={creatingSrtStream}
										onClick={handleOpenInputTypeSelector}
									>
										Choose Endpoint
										<Icon name={inputTypeSelectorOpen ? ICONS.CARROT_UP : ICONS.CARROT_DOWN} size={dropdownIconSize} color={COLORS.TEXT_GRAY_200} />
									</button>
									{inputTypeSelectorOpen && (
										<div className="input-type-selector-menu">
											{(['srt', 'whip', 'rtmp'] as const).map((type) => (
												<button
													key={type}
													className="input-type-selector-option"
													onClick={() => handleSetInputType(type)}
												>
													<span className="bl-font-p2">
														{type === 'srt' ? 'SRT' : LIVEKIT_INGRESS_INPUT_LABELS[type]}
													</span>
													<span className="input-type-selector-arrow">
														<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={dropdownIconSize} color={COLORS.TEXT_GRAY_200} />
													</span>
												</button>
											))}
										</div>
									)}
								</div>
							) : (
								<div className="action-slot">
									<button
										className={classNames('meta-panel-action-button', 'bl-font-l1', 'local-bttn-critical-default', 'grayscale-1200', 'semantic-red-bg-400')}
										disabled={creatingSrtStream}
										onClick={() => handleSetInputType('cam_mic')}
									>
										<Icon name={ICONS.MEDIA_DELETE} size={16} color={COLORS.TEXT_GRAY_1200} />
										{`Hide ${ingressInputType ? LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType] : 'SRT'} Endpoint`}
									</button>
								</div>
							)
						) : (
							<div className="action-slot">
								<button
									className={classNames('meta-panel-action-button', 'bl-font-l1', {
										'local-bttn-critical-default': selectedLocation.useSrt,
										'grayscale-1200': selectedLocation.useSrt,
										'semantic-red-bg-400': selectedLocation.useSrt,
									})}
									disabled={creatingSrtStream}
									onClick={handleToggleSrt}
								>
									<Icon
										name={selectedLocation.useSrt ? ICONS.MEDIA_DELETE : ICONS.ADD}
										size={16}
										color={selectedLocation.useSrt ? COLORS.TEXT_GRAY_1200 : COLORS.TEXT_GRAY_200}
									/>
									{selectedLocation.useSrt ? 'Hide SRT Endpoint' : 'Add SRT Endpoint'}
								</button>
							</div>
						)}
					</div>
				</div>
			</div >
		)
	);
}
